import { useMemo, useSyncExternalStore } from 'react';
import { JsonPointerPath } from 'types/dynamic-forms/JsonPointerPath';
import { useDocumentStore } from './useDocumentStore';

export default function useProperty<T = any>(path: JsonPointerPath) {
  const store = useDocumentStore();

  const callbacks = useMemo(() => {
    const getActualState = () => {
      // Optimization opportunity: extract DocumentProperty only once per state change.
      // Currently, the 'path' is traversed multiple times.
      const value = store.getValue(path) as T;
      const errorMessage = store.getErrorMessage(path);
      const isVisible = store.getIsVisible(path);
      const isDisabled = store.getIsDisabled(path);
      return { value, errorMessage, isVisible, isDisabled };
    };

    let prevState = getActualState();

    const isSameState = (a: ReturnType<typeof getActualState>, b: ReturnType<typeof getActualState>) =>
      Object.is(a.value, b.value) &&
      Object.is(a.errorMessage, b.errorMessage) &&
      Object.is(a.isVisible, b.isVisible) &&
      Object.is(a.isDisabled, b.isDisabled);

    return {
      subscribe: (callback: () => void) => {
        const memoizedCallback = () => {
          const newState = getActualState();
          if (!isSameState(prevState, newState)) {
            prevState = newState;
            callback();
          }
        };

        return store.addDataChangedListener(memoizedCallback);
      },
      getState: () => prevState,
      setValue: (value: T) => store.setValue(path, value),
    };
  }, [store, ...path]);
  const state = useSyncExternalStore(callbacks.subscribe, callbacks.getState);

  return { ...state, setValue: callbacks.setValue };
}
